import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutGrid, Truck, Cpu, Flame, MapPin, Wrench, Briefcase,
  Brain, Upload, FileText, Settings, Mountain, LogOut,
} from "lucide-react";
import OpsoraWordmark from "@/components/ui-fleet/OpsoraWordmark";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABELS = {
  admin: "Administrator",
  fleet_manager: "Fleet Manager",
  dealer: "Dealer",
  service_engineer: "Service Engineer",
};

// Single source of truth for nav visibility per role
const navMain = [
  { to: "/dashboard", icon: LayoutGrid, label: "Command Center",      testId: "nav-dashboard", roles: ["admin", "fleet_manager", "dealer"] },
  { to: "/fleet",     icon: Truck,      label: "Fleet Monitor",       testId: "nav-fleet",     roles: ["admin", "fleet_manager", "dealer", "service_engineer"] },
  { to: "/engine",    icon: Cpu,        label: "Engine Analytics",    testId: "nav-engine",    roles: ["admin", "fleet_manager"] },
  { to: "/fuel",      icon: Flame,      label: "Fuel & Energy",       testId: "nav-fuel",      roles: ["admin", "fleet_manager"] },
  { to: "/geo",       icon: MapPin,     label: "Geolocation",         testId: "nav-geo",       roles: ["admin", "fleet_manager"] },
  { to: "/service",   icon: Wrench,     label: "Service Hub",         testId: "nav-service",   roles: ["admin", "fleet_manager", "dealer", "service_engineer"] },
  { to: "/sales",     icon: Briefcase,  label: "Sales & CRM",         testId: "nav-sales",     roles: ["admin", "dealer"] },
  { to: "/dumper",    icon: Mountain,   label: "Dumper Intel",        testId: "nav-dumper",    roles: ["admin", "fleet_manager"] },
];

const navTools = [
  { to: "/ai",      icon: Brain,    label: "AI Assistant",   testId: "nav-ai",      roles: ["admin", "fleet_manager", "dealer", "service_engineer"] },
  { to: "/import",  icon: Upload,   label: "Data Import",    testId: "nav-import",  roles: ["admin"] },
  { to: "/reports", icon: FileText, label: "Reports",        testId: "nav-reports", roles: ["admin", "fleet_manager", "dealer", "service_engineer"] },
];

export default function Sidebar({ alertCount = 0, connectionStatus = "disconnected" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const role = user?.role || "admin";

  const visibleMain = navMain.filter((n) => n.roles.includes(role));
  const visibleTools = navTools.filter((n) => n.roles.includes(role));

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const statusConfig = {
    connected:       { color: "#2D7A4A", label: "Live",            pulse: true  },
    connecting:      { color: "#B07D1A", label: "Connecting…",     pulse: false },
    disconnected:    { color: "#7A726A", label: "Reconnecting…",   pulse: false },
    unauthenticated: { color: "#7A726A", label: "Offline",         pulse: false },
    error:           { color: "#B03A2A", label: "Stream error",    pulse: false },
  };
  const sc = statusConfig[connectionStatus] || statusConfig.disconnected;

  return (
    <aside
      className="glass-sidebar fixed left-0 top-0 bottom-0 w-[240px] flex flex-col z-30"
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="px-5 pt-6 pb-3 flex items-center gap-2.5">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{
            background: "linear-gradient(135deg, #E8884A 0%, #C4622D 100%)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5), 0 4px 14px rgba(196, 98, 45, 0.4)",
          }}
        >
          <span className="text-white font-display text-[19px] leading-none" style={{ fontWeight: 600 }}>O</span>
        </div>
        <OpsoraWordmark size="md" />
      </div>

      {/* Live connection status */}
      <div className="px-5 pb-3 flex items-center gap-1.5" data-testid="ws-status">
        <span
          className="rounded-full"
          style={{
            width: 7,
            height: 7,
            background: sc.color,
            boxShadow: sc.pulse ? `0 0 0 0 ${sc.color}55` : "none",
            animation: sc.pulse ? "pulse 2s infinite" : "none",
          }}
        />
        <span className="text-[10.5px]" style={{ color: "var(--slate-500)", letterSpacing: "0.04em" }}>{sc.label}</span>
      </div>

      <div className="h-[1px] mx-5 mb-3" style={{ background: "var(--glass-border-btm)" }} />

      {/* Main nav */}
      <nav className="flex-1 overflow-y-auto px-3 pb-3">
        <div className="px-3 mb-2 text-[10px] uppercase tracking-widest" style={{ color: "var(--slate-500)" }}>
          Operations
        </div>
        {visibleMain.map(({ to, icon: Icon, label, testId, badge }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testId}
            className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
          >
            <Icon size={16} />
            <span className="flex-1">{label}</span>
            {badge && (
              <span
                className="ml-auto inline-flex items-center justify-center px-1.5 py-[1px] rounded text-[9px] font-semibold"
                style={{
                  background: "rgba(59, 111, 168, 0.12)",
                  color: "#3B6FA8",
                  border: "1px solid rgba(59,111,168,0.25)",
                  letterSpacing: "0.05em",
                }}
              >
                {badge}
              </span>
            )}
            {to === "/dashboard" && alertCount > 0 && (
              <span
                className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-[10px] font-mono font-semibold"
                style={{
                  background: "var(--status-red)",
                  color: "white",
                  boxShadow: "0 2px 8px rgba(176, 58, 42, 0.35)",
                }}
                data-testid="sidebar-alert-badge"
              >
                {alertCount}
              </span>
            )}
          </NavLink>
        ))}

        {visibleTools.length > 0 && (
          <>
            <div className="h-[1px] my-3 mx-2" style={{ background: "var(--glass-border-btm)" }} />
            <div className="px-3 mb-2 text-[10px] uppercase tracking-widest" style={{ color: "var(--slate-500)" }}>
              Tools
            </div>
            {visibleTools.map(({ to, icon: Icon, label, testId }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={testId}
                className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              >
                <Icon size={16} />
                <span>{label}</span>
              </NavLink>
            ))}
          </>
        )}

        <div className="h-[1px] my-3 mx-2" style={{ background: "var(--glass-border-btm)" }} />

        <NavLink
          to="/settings"
          data-testid="nav-settings"
          className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
        >
          <Settings size={16} />
          <span>Settings</span>
        </NavLink>
      </nav>

      {/* User footer */}
      {user && (
        <div className="px-4 py-3" style={{ borderTop: "1px solid var(--glass-border-btm)" }} data-testid="sidebar-user">
          <div className="flex items-center gap-2.5 mb-2">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-[12px]"
              style={{
                background: "linear-gradient(135deg, #E8884A 0%, #C4622D 100%)",
              }}
            >
              {(user.name || user.email || "?").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] truncate" style={{ color: "var(--slate-900)", fontWeight: 600 }} data-testid="sidebar-user-name">
                {user.name}
              </div>
              <div className="text-[10.5px]" style={{ color: "var(--slate-500)" }} data-testid="sidebar-user-role">
                {ROLE_LABELS[user.role] || user.role}
              </div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="sidebar-logout-btn"
              className="p-1.5 rounded hover:bg-black/5 transition-colors"
              style={{ color: "var(--slate-500)" }}
              title="Sign out"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Kairoo Attribution */}
      <div
        className="px-5 py-3 hidden md:block"
        style={{ borderTop: "1px solid var(--glass-border-btm)" }}
        data-testid="kairoo-attribution"
      >
        <p
          className="font-body"
          style={{
            fontSize: "11px",
            fontWeight: 600,
            color: "var(--slate-700)",
            letterSpacing: "0.01em",
            margin: 0,
            marginBottom: "3px",
          }}
        >
          Engineered by Kairoo Technologies
        </p>
        <p
          className="font-body"
          style={{
            fontSize: "11px",
            fontWeight: 400,
            color: "var(--slate-500)",
            letterSpacing: "0.01em",
            margin: 0,
          }}
        >
          Your Technology Partner
        </p>
      </div>
    </aside>
  );
}
